import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
    Box,
    Container,
    Typography,
    Grid,
    Paper,
    Button,
    Stack,
    Chip,
    Avatar,
    Divider,
    CircularProgress,
    useTheme,
    IconButton
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import LockIcon from "@mui/icons-material/Lock";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import StarIcon from "@mui/icons-material/Star";
import VerifiedIcon from "@mui/icons-material/Verified";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import axiosInstance from "../api/axiosInstance";
import { getCourseImage } from "../utils/imageUtils";
import { useSelector } from "react-redux";
import buildHierarchy from "../components/body/buildHirerchy";

const CourseCurriculum = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const { user } = useSelector((state) => state.auth);

    const [course, setCourse] = useState(null);
    const [hierarchicalContents, setHierarchicalContents] = useState([]);
    const [enrolled, setEnrolled] = useState(false);
    const [loading, setLoading] = useState(true);
    const [expandedModules, setExpandedModules] = useState({});

    const colors = {
        primary: "#e50914",
        secondary: "#6a0dad",
        accent: "#A380FF",
        success: "#00e676",
        bg: isDark ? "#0a0a0a" : "#f8f9fa",
    };

    useEffect(() => {
        const fetchCourseData = async () => {
            try {
                const normalizedSlug = slug?.toLowerCase() === "agenticai" ? "agentic-ai" : slug;
                const courseRes = await axiosInstance.get(`/course/${normalizedSlug}`);
                const contentsRes = await axiosInstance.get(`/post/contents?subject=${normalizedSlug}`);

                const courseData = courseRes.data;
                const hierarchy = buildHierarchy(contentsRes.data);

                setCourse(courseData);
                setHierarchicalContents(hierarchy);

                if (user) {
                    const enrollRes = await axiosInstance.get("/course/enrollments/me");
                    const isEnrolled = enrollRes.data.some(e => e.course_id === courseData.id);
                    setEnrolled(isEnrolled);
                }

                // Auto-expand first module
                if (hierarchy.length > 0) {
                    setExpandedModules({ [hierarchy[0].content_id]: true });
                }
            } catch (err) {
                console.error("Error fetching curriculum:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchCourseData();
    }, [slug, user]);

    const toggleModule = (id) => {
        setExpandedModules(prev => ({ ...prev, [id]: !prev[id] }));
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "80vh" }}>
                <CircularProgress size={60} sx={{ color: colors.primary }} />
            </Box>
        );
    }

    if (!course) {
        return (
            <Container sx={{ py: 10, textAlign: "center" }}>
                <Typography variant="h4">Course not found</Typography>
                <Button onClick={() => navigate("/")} sx={{ mt: 2 }}>Back to Home</Button>
            </Container>
        );
    }

    const handleEnroll = async () => {
        if (!user) {
            navigate("/signin");
            return;
        }

        if (course.is_paid) {
            navigate("/");
        } else {
            try {
                await axiosInstance.post(`/payment/enroll-free/${course.id}`);
                setEnrolled(true);
            } catch (err) {
                alert("Enrollment failed");
            }
        }
    };

    const isLocked = course.is_paid && !enrolled && user?.role !== "admin";

    const renderLesson = (content, depth = 0) => {
        const hasChildren = content.children && content.children.length > 0;
        const isExpanded = expandedModules[content.content_id];

        if (hasChildren) {
            // Module / Parent Item
            return (
                <Box key={content.content_id} sx={{ mb: 2 }}>
                    <Paper
                        elevation={0}
                        onClick={() => toggleModule(content.content_id)}
                        sx={{
                            p: 2, borderRadius: 3, cursor: "pointer",
                            bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
                            border: `1px solid ${isExpanded ? colors.primary : "transparent"}`,
                            transition: "all 0.3s ease",
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            "&:hover": {
                                bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
                            }
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ flex: 1 }}>
                            <Avatar sx={{ bgcolor: isExpanded ? colors.primary : "transparent", color: isExpanded ? "#fff" : colors.primary, width: 36, height: 36, border: `1px solid ${colors.primary}` }}>
                                <Typography variant="subtitle2" fontWeight={900}>{content.content_id}</Typography>
                            </Avatar>
                            <Box>
                                <Typography variant="subtitle1" fontWeight={800}>
                                    {content.title}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {content.children.length} Lessons
                                </Typography>
                            </Box>
                        </Stack>
                        <ExpandMoreIcon sx={{
                            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.3s ease",
                            color: isExpanded ? colors.primary : "text.secondary"
                        }} />
                    </Paper>

                    {isExpanded && (
                        <Box sx={{ mt: 1, ml: { xs: 2, md: 4 }, borderLeft: `2px solid ${colors.primary}20`, pl: 2 }}>
                            <Stack spacing={1}>
                                {content.children.map(child => renderLesson(child, depth + 1))}
                            </Stack>
                        </Box>
                    )}
                </Box>
            );
        }

        // Leaf Lesson
        return (
            <Paper
                key={content.content_id}
                elevation={0}
                onClick={() => !isLocked ? navigate(`/${course.slug}/${content.content_id}`) : null}
                sx={{
                    p: 1.5, borderRadius: 2, cursor: isLocked ? "default" : "pointer",
                    bgcolor: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    transition: "all 0.2s ease",
                    "&:hover": {
                        transform: isLocked ? "none" : "translateX(5px)",
                        bgcolor: isLocked ? undefined : (isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)")
                    }
                }}
            >
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="body2" fontWeight={700} sx={{ opacity: 0.4, minWidth: 25 }}>
                        {content.content_id}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                        {content.title}
                    </Typography>
                </Stack>

                {isLocked ? (
                    <LockIcon sx={{ fontSize: 16, color: "text.secondary", opacity: 0.5 }} />
                ) : (
                    <PlayArrowIcon sx={{ fontSize: 18, color: colors.primary }} />
                )}
            </Paper>
        );
    };

    return (
        <Box sx={{ bgcolor: colors.bg, minHeight: "100vh", pb: 10 }}>
            {/* 🚀 Hero Section */}
            <Box
                sx={{
                    background: isDark
                        ? `linear-gradient(135deg, ${colors.primary}20 0%, ${colors.secondary}20 100%)`
                        : `linear-gradient(135deg, ${colors.primary}10 0%, ${colors.secondary}10 100%)`,
                    pt: { xs: 10, md: 15 }, pb: 8, borderBottom: `1px solid ${theme.palette.divider}`
                }}
            >
                <Container maxWidth="lg">
                    <IconButton onClick={() => navigate("/")} sx={{ mb: 4, color: "text.secondary" }}>
                        <ArrowBackIcon />
                    </IconButton>

                    <Grid container spacing={6} alignItems="center">
                        <Grid size={{ xs: 12, md: 7 }}>
                            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                <Chip label={course.category} size="small" sx={{ bgcolor: colors.primary, color: "#fff", fontWeight: 700 }} />
                                {course.is_paid && <Chip label="Premium" size="small" sx={{ bgcolor: colors.accent, color: "#fff", fontWeight: 700 }} />}
                            </Stack>

                            <Typography variant="h2" fontWeight={900} sx={{ mb: 3, lineHeight: 1.1, fontSize: { xs: "2.5rem", md: "3.5rem" } }}>
                                {course.title}
                            </Typography>

                            <Typography variant="h6" sx={{ mb: 4, color: "text.secondary", fontWeight: 400, lineHeight: 1.6 }}>
                                {course.description}
                            </Typography>

                            <Stack direction="row" spacing={4} sx={{ mb: 5 }}>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={800} color="primary">LEVEL</Typography>
                                    <Typography variant="body1" fontWeight={600}>Beginner to Pro</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={800} color="primary">LESSONS</Typography>
                                    <Typography variant="body1" fontWeight={600}>{hierarchicalContents.length} Major Modules</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={800} color="primary">ACCESS</Typography>
                                    <Typography variant="body1" fontWeight={600}>Lifetime</Typography>
                                </Box>
                            </Stack>

                            {!enrolled ? (
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={handleEnroll}
                                    sx={{
                                        px: 6, py: 2, borderRadius: "14px", fontWeight: 800, fontSize: "1.1rem",
                                        background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                                        boxShadow: `0 10px 30px ${colors.primary}40`
                                    }}
                                >
                                    {course.is_paid ? `Unlock Entire Course @ ₹${course.price}` : "Enroll and Start Learning"}
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    size="large"
                                    disabled
                                    sx={{ px: 6, py: 2, borderRadius: "14px", bgcolor: colors.success, color: "#fff", "&.Mui-disabled": { bgcolor: colors.success, color: "#fff", opacity: 1 } }}
                                    startIcon={<VerifiedIcon />}
                                >
                                    You are Enrolled
                                </Button>
                            )}
                        </Grid>

                        <Grid size={{ xs: 12, md: 5 }}>
                            <Paper
                                elevation={24}
                                sx={{
                                    borderRadius: 6, overflow: "hidden",
                                    transform: { md: "rotate(3deg) scale(1.05)" },
                                    border: `4px solid ${isDark ? "rgba(255,255,255,0.05)" : "#fff"}`,
                                    transition: "transform 0.5s ease",
                                    "&:hover": { transform: "rotate(0deg) scale(1.1)" }
                                }}
                            >
                                <Box component="img" src={getCourseImage(course)} sx={{ width: "100%", display: "block" }} />
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* 📚 Curriculum Content */}
            <Container maxWidth="md" sx={{ mt: -6, position: "relative", zIndex: 2 }}>
                <Paper
                    sx={{
                        p: { xs: 3, md: 5 }, borderRadius: 6,
                        background: isDark ? "rgba(20,20,30,0.95)" : "#fff",
                        backdropFilter: "blur(20px)",
                        border: `1px solid ${theme.palette.divider}`,
                        boxShadow: "0 20px 50px rgba(0,0,0,0.1)"
                    }}
                >
                    <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="h5" fontWeight={900} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <MenuBookIcon sx={{ color: colors.primary }} /> Detailed Syllabus
                        </Typography>
                        <Chip label={`${hierarchicalContents.length} Modules`} variant="outlined" size="small" />
                    </Box>

                    <Box>
                        {hierarchicalContents.length > 0 ? (
                            hierarchicalContents.map(module => renderLesson(module))
                        ) : (
                            <Box sx={{ py: 10, textAlign: "center", opacity: 0.5 }}>
                                <Typography>The detailed syllabus is being updated. Stay tuned!</Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Info Card for Non-Enrolled */}
                    {!enrolled && course.is_paid && (
                        <Box
                            sx={{
                                mt: 6, p: 4, borderRadius: 5, bgcolor: `${colors.secondary}10`,
                                border: `1px dashed ${colors.secondary}`, textAlign: "center"
                            }}
                        >
                            <Typography variant="h6" fontWeight={800} gutterBottom>Ready to unlock this content?</Typography>
                            <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
                                Join thousands of students and get lifetime access to all modules, code files, and community support.
                                Master {course.title} today.
                            </Typography>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={handleEnroll}
                                sx={{ borderRadius: "10px", fontWeight: 700, px: 4 }}
                            >
                                Enroll Now
                            </Button>
                        </Box>
                    )}
                </Paper>
            </Container>
        </Box>
    );
};

export default CourseCurriculum;
